import React, { useState, useEffect, useRef } from "react";
import { GatewayData } from "../types";
import { useAuth } from "../contexts/AuthContext";
import GoogleMapLocation from "./GoogleMapLocation";
import ExportDropdown from "./ExportDropdown";
import { fetchGatewayDataFromSupabase, fetchMultipleDtuHistoryFromSupabase, fetchAllDtuHistoryGrouped, clearCachedAllDtuHistoryGrouped } from "../lib/supabaseDataProcessing";

interface DashboardProps {
  initialGatewayData: GatewayData[];
  mapSettings?: {
    center?: { lat: number; lng: number };
    zoom?: number;
  };
}

interface LogEntry {
  timestamp: Date;
  normalCount: number;
  alertCount: number;
  isAlert: boolean;
  alertingSensors: string[]; // Array of sensors with alert format "GW-x-SensorY"
}

const Dashboard: React.FC<DashboardProps> = ({
  initialGatewayData: initialGatewayData,
  mapSettings,
}) => {
  const { logout } = useAuth();
  const [gatewayData, setSensorData] =
    useState<GatewayData[]>(initialGatewayData);

  // Update gateway data when props change
  useEffect(() => {
    setSensorData(computeGatewayMetricsAll(initialGatewayData));
  }, [initialGatewayData]);
  const [brokenSensorCount, setBrokenSensorCount] = useState<number>(0);
  const [totalSensorCount, setTotalSensorCount] = useState<number>(0);
  const [lastPageUpdate, setLastPageUpdate] = useState<Date>(new Date());
  const [lastDataFetch, setLastDataFetch] = useState<Date>(new Date());
  const [showLogBox, setShowLogBox] = useState<boolean>(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [selectedDtu, setSelectedDtu] = useState<string>("all"); // New state for DTU selection
  const [attentionTab, setAttentionTab] = useState<'alert' | 'disconnected' | 'broken'>('disconnected');
  const [historyTab, setHistoryTab] = useState<'latest' | 'history'>('latest');
  const [dtuHistory, setDtuHistory] = useState<GatewayData[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [allDtuHistoryGroups, setAllDtuHistoryGroups] = useState<GatewayData[][]>([]);
  const [loadingAllHistory, setLoadingAllHistory] = useState(false);
  const [sortSpecs, setSortSpecs] = useState<Array<[string, SortDirection]>>([['name', 'ascending']]);
  const [sortOpen, setSortOpen] = useState<boolean>(false);



  // Use the provided map settings (DTU coordinates are now set in environment variables)
  const updatedMapSettings = mapSettings;

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Check on mount
    checkMobile();

    // Check on resize
    window.addEventListener("resize", checkMobile);

    // Cleanup
    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  // Calculate statistics
  useEffect(() => {
    const filteredData = getFilteredGatewayData();
    if (filteredData && filteredData.length > 0) {
      // Count non-zero values in data arrays as "filled", but only for working sensors
      let filledCount = 0;
      let totalCount = 0;
      let workingSensorCount = 0;
      let totalSensorCount = 0;

      // Find the most recent timestamp from all gateway data
      let latestTimestamp = new Date(0); // Start with epoch time
      filteredData.forEach((gateway: GatewayData) => {
        // Check if gateway has a valid timestamp/time
        const gatewayTime = gateway.timestamp || gateway.time;
        if (gatewayTime) {
          const gatewayDate = new Date(gatewayTime);
          if (gatewayDate > latestTimestamp) {
            latestTimestamp = gatewayDate;
          }
        }

        if (gateway && gateway.nodes && Array.isArray(gateway.nodes)) {
          gateway.nodes.slice(0, 10).forEach((value, index) => {
            const isWorking =
              gateway.nodes_status &&
              Array.isArray(gateway.nodes_status) &&
              gateway.nodes_status.length > index &&
              gateway.nodes_status[index] !== 0;

            totalSensorCount++;
            if (isWorking) {
              workingSensorCount++;
              totalCount++;
              if (value !== 0) {
                filledCount++;
              }
            }
          });
        }
      });

      // Store total sensor count based on filtered data
      setTotalSensorCount(totalSensorCount);

      // Calculate broken sensors based on actual data
      let brokenCount = 0;
      filteredData.forEach((gateway: GatewayData) => {
        if (
          gateway &&
          gateway.nodes_status &&
          Array.isArray(gateway.nodes_status)
        ) {
          gateway.nodes_status.slice(0, 10).forEach((status) => {
            if (status === 0) {
              brokenCount++;
            }
          });
        }
      });
      setBrokenSensorCount(brokenCount);

      // Use the latest timestamp from data instead of current time
      setLastPageUpdate(latestTimestamp);

      // Add a log entry when data is updated
      const normalCount = countFilledGateways();
      const alertCount = countEmptyGateways();
      const isAlert = alertCount > 0;

      // Consistency check to ensure normal + alerting + broken = total sensors

      // Get list of alerting sensors
      const alertingSensors: string[] = [];
      filteredData.forEach((gateway: GatewayData) => {
        if (gateway && gateway.nodes && Array.isArray(gateway.nodes)) {
          gateway.nodes.slice(0, 10).forEach((value, index) => {
            const isWorking =
              gateway.nodes_status &&
              Array.isArray(gateway.nodes_status) &&
              gateway.nodes_status.length > index &&
              gateway.nodes_status[index] !== 0;

            // Add to alerting sensors if working but value is zero (alerting)
            if (isWorking && value === 0) {
              alertingSensors.push(`${gateway.alias}-Sensor${index + 1}`);
            }
          });
        }
      });

      setLogs((prevLogs) => [
        {
          timestamp: latestTimestamp,
          normalCount,
          alertCount,
          isAlert,
          alertingSensors,
        },
        ...prevLogs.slice(0, 49), // Keep only the 50 most recent logs
      ]);
    }
  }, [gatewayData, selectedDtu]);

  /*HELPER FUNCTIONS*/

  type SortDirection = 'ascending' | 'descending';

  function sortGatewaysBy(
    gateways: GatewayData[],
    keys: Array<string | [string, SortDirection]>,
    defaultDirection: SortDirection = 'descending'
  ): GatewayData[] {
    const keyList = keys;
    const order = (a: number, b: number) => (a < b ? -1 : a > b ? 1 : 0);
    const locale = (a?: string, b?: string) => (a || '').localeCompare(b || '');

    const statusOrder: Record<string, number> = {
      normal: 0,
      alert: 1,
      malfunction: 2,
      disconnected: 3,
    };

    // normalize -> [key, direction]
    const normalizedKeys: Array<[string, SortDirection]> = keys.map((k) =>
      typeof k === 'string' ? [k, defaultDirection] : k
    );

    // Start from a shallow copy
    let result = gateways.slice();

    // Stable multi-key sort: apply least-significant key first
    [...normalizedKeys].slice().reverse().forEach(([rawKey, dir]) => {
      const k = (rawKey || '').toLowerCase().trim();

      // Force ascending when sorting by name
      const mul = dir === 'ascending' ? 1 : -1;

      result = result.slice().sort((a: GatewayData, b: GatewayData) => {
        let cmp = 0;
        switch (k) {
          case 'number of normal sensor':
          case 'normal':
            cmp = order((a.normalCount ?? 0), (b.normalCount ?? 0));
            break;
          case 'number of alert sensor':
          case 'alert':
            cmp = order((a.alertCount ?? 0), (b.alertCount ?? 0));
            break;
          case 'number of malfunction sensor':
          case 'malfunction':
            cmp = order((a.brokenCount ?? 0), (b.brokenCount ?? 0));
            break;
          case 'dtu status':
          case 'status':
            cmp = order(statusOrder[a.dtuStatus ?? 'disconnected'], statusOrder[b.dtuStatus ?? 'disconnected']);
            break;
          case 'dtu name':
          case 'name':
            cmp = locale(a.alias ?? a.gateway_id, b.alias ?? b.gateway_id);
            break;
          case 'time':
          case 'timestamp':
            cmp = order((a.timestamp ?? 0), (b.timestamp ?? 0));
            break;
          default:
            cmp = 0;
        }
        return mul * cmp;
      });
    });

    return result;
  }

  function computeGatewayMetricsAll(gateways: GatewayData[]): GatewayData[] {
    const now = Date.now();

    return gateways.map((g) => {
      const nodes = Array.isArray(g.nodes) ? g.nodes.slice(0, 10) : [];
      const nodes_status = Array.isArray(g.nodes_status) ? g.nodes_status.slice(0, 10) : [];

      const normalCount = nodes.reduce((acc, val, idx) => {
        const working = nodes_status[idx] !== 0;
        return acc + (working && val !== 0 ? 1 : 0);
      }, 0);

      const alertCount = nodes.reduce((acc, val, idx) => {
        const working = nodes_status[idx] !== 0;
        return acc + (working && val === 0 ? 1 : 0);
      }, 0);

      const brokenCount = nodes_status.reduce((acc, s) => acc + (s === 0 ? 1 : 0), 0);

      const last =
        typeof g.timestamp === "number"
          ? g.timestamp
          : g.time
          ? new Date(g.time).getTime()
          : 0;

      const hoursDiff = last ? (now - last) / (1000 * 60 * 60) : Infinity;
      // console.log(g.time, g.timestamp, new Date(g.timestamp).toISOString());
      let dtuStatus: GatewayData["dtuStatus"] = "normal";
      if (hoursDiff >= 24) {
        dtuStatus = "disconnected";
      } else if (brokenCount > 0) {
        dtuStatus = "malfunction";
      } else if (alertCount > 0) {
        dtuStatus = "alert";
      } else {
        dtuStatus = "normal";
      }

      return {
        ...g,
        normalCount,
        alertCount,
        brokenCount,
        dtuStatus,
        lastUpdate: last || undefined,
      } as GatewayData;
    });
  }

    // --- Sort UI helpers ---
  const sortableKeyDefs: {
    key: string;
    title: string;
    desc: string;
  }[] = [
    { key: 'name', title: 'DTU Name', desc: 'Sort by DTU name' },
    { key: 'timestamp', title: 'Time', desc: 'Sort by data online time' },
    { key: 'status', title: 'DTU Status', desc: 'Normal / Alert / Malfunction / Disconnected' },
    { key: 'normal', title: 'Normal Sensors', desc: 'Number of normal sensors' },
    { key: 'alert', title: 'Alert Sensors', desc: 'Number of low-level sensors' },
    { key: 'malfunction', title: 'Malfunction Sensors', desc: 'Number of malfunction sensors' },
  ];

  // helper: get remaining keys (not currently selected)
  const getRemainingKeys = () =>
    sortableKeyDefs
      .map((d) => d.key)
      .filter((k) => !sortSpecs.some((s) => s[0] === k));

  // add or move key to end (clicking bottom list)
  const addOrBringKeyToTop = (key: string) => {
    setSortSpecs((prev: Array<[string, SortDirection]>) => {
      const next: Array<[string, SortDirection]> = prev.filter((s) => s[0] !== key);
      // default direction when user adds a new key: descending
      next.push([key, 'descending']);
      return next.length === 0 ? [['name', 'ascending']] : next;
    });
  };

  // toggle direction for the spec at index (tuple-safe)
  const toggleSpecDirection = (index: number) => {
    setSortSpecs((prev: Array<[string, SortDirection]>) => {
      const next = prev.map(([key, dir], i): [string, SortDirection] => {
        if (i !== index) return [key, dir];
        const newDir: SortDirection = dir === 'ascending' ? 'descending' : 'ascending';
        return [key, newDir];
      });
      return next.length === 0 ? [['name', 'ascending']] : next;
    });
  };

  // remove a key from current specs
  const removeSpecAt = (index: number) => {
    setSortSpecs((prev: Array<[string, SortDirection]>) => {
      const next = prev.slice();
      next.splice(index, 1);
      return next.length === 0 ? [['name', 'ascending']] : next;
    });
  };

  // clear sorting (reset to default)
  const resetSortingToDefault = () => {
    setSortSpecs([['name', 'ascending']]);
  };

  // SortDropdown component (parent-controlled open state: `sortOpen`)
  const SortDropdown: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
    const dropdownRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      const handleOutside = (e: MouseEvent) => {
        if (!sortOpen) return;
        if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
          setSortOpen(false);
        }
      };
      document.addEventListener('mousedown', handleOutside);
      return () => document.removeEventListener('mousedown', handleOutside);
    }, [sortOpen]);

    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setSortOpen((v) => !v)}
          className={`${compact ? 'px-2 py-1 text-xs' : 'px-3 py-1 text-sm'} border rounded bg-white shadow hover:bg-gray-100 hover:shadow-md ${sortOpen ? 'bg-gray-100 ring-2 ring-blue-300' : ''}`}
          title="Sort gateways"
        >
          Sort
        </button>

        {sortOpen && (
          <div className={`absolute right-0 mt-2 ${compact ? 'w-40 text-xs' : 'w-80 text-sm'} bg-white border rounded shadow-lg z-50 p-3`}>
            <div className="flex items-center justify-between mb-2">
              <div className="font-medium">Active Sort Keys</div>
              <div className="flex gap-2">
                <button
                  onClick={() => { resetSortingToDefault(); }}
                  className="px-2 py-1 text-xs rounded bg-white hover:bg-gray-100 active:bg-gray-200 text-blue-600"
                >
                  Reset
                </button>
                <button
                  onClick={() => setSortOpen(false)}
                  className="px-2 py-1 text-xs rounded bg-white hover:bg-gray-100 active:bg-gray-200"
                >
                  Close
                </button>
              </div>
            </div>

            <div className="mb-3">
              {sortSpecs.map(([k, dir], idx) => {
                const def = sortableKeyDefs.find((d) => d.key === k);
                return (
                  <div key={k} className="flex items-center justify-between gap-2 mb-2 p-1 rounded hover:bg-gray-100 active:bg-gray-200">
                    <div className="flex-1">
                      <div className="font-medium">{def?.title || k}</div>
                      <div className="text-xs text-gray-500">{def?.desc || ''}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                          onClick={() => toggleSpecDirection(idx)}
                          className="px-2 py-1 border rounded text-xs hover:bg-gray-100 active:bg-gray-200"
                          title="Toggle direction"
                        >
                        {dir === 'ascending' ? '↑' : '↓'}
                      </button>
                      <button onClick={() => removeSpecAt(idx)} className="px-2 py-1 border rounded text-xs hover:bg-gray-100 active:bg-gray-200">✕</button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="border-t pt-2">
              <div className="font-medium mb-2">Available Keys</div>
              <div className="grid gap-1">
                {getRemainingKeys().map((k) => {
                  const def = sortableKeyDefs.find((d) => d.key === k);
                  return (
                    <button
                      key={k}
                      onClick={() => addOrBringKeyToTop(k)}
                      className="flex items-center justify-between p-2 rounded hover:bg-gray-100 active:bg-gray-200 text-left cursor-pointer"
                    >
                      <div>
                        <div className="font-medium">{def?.title || k}</div>
                        <div className="text-xs text-gray-500">{def?.desc || ''}</div>
                      </div>
                      <div className="text-xs text-gray-400">Add</div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };


  // Update data fetch timestamp when gateway data changes
  useEffect(() => {
    setLastDataFetch(new Date());
  }, [gatewayData]);

  // Format date as YYYY/MM/DD HH:mm:ss using local time to match client-side rendering
  const formatDate = (date: Date | string | number): string => {
    const dateObj =
      typeof date === "string" || typeof date === "number"
        ? new Date(date)
        : date;
    return `${dateObj.getFullYear()}/${(dateObj.getMonth() + 1)
      .toString()
      .padStart(2, "0")}/${dateObj
      .getDate()
      .toString()
      .padStart(2, "0")} ${dateObj
      .getHours()
      .toString()
      .padStart(2, "0")}:${dateObj.getMinutes().toString().padStart(2, "0")}:${dateObj.getSeconds().toString().padStart(2, "0")}`;
  };

  // Get the first 10 data points
  const getWaterStatus = (gatewayId: string, sensorIndex: number): boolean => {
    const filteredData = getFilteredGatewayData();
    const gateway = filteredData.find((s) => s.gateway_id === gatewayId);
    if (
      gateway &&
      gateway.nodes &&
      Array.isArray(gateway.nodes) &&
      gateway.nodes.length > sensorIndex
    ) {
      const isWorking =
        gateway.nodes_status &&
        Array.isArray(gateway.nodes_status) &&
        gateway.nodes_status.length > sensorIndex &&
        gateway.nodes_status[sensorIndex] !== 0;

      // Only consider the node value if the sensor is working
      return isWorking && gateway.nodes[sensorIndex] !== 0;
    }
    return false;
  };

  // Get the node status (working or not)
  const getNodeStatus = (gatewayId: string, sensorIndex: number): boolean => {
    const filteredData = getFilteredGatewayData();
    const gateway = filteredData.find((s) => s.gateway_id === gatewayId);
    if (
      gateway &&
      gateway.nodes_status &&
      Array.isArray(gateway.nodes_status) &&
      gateway.nodes_status.length > sensorIndex
    ) {
      return gateway.nodes_status[sensorIndex] !== 0;
    }
    return false;
  };

  // Get the display name (alias or gateway_id)
  const getGatewayDisplayName = (gateway: GatewayData): string => {
    return gateway.alias;
  };

  const countFilledGateways = (): number => {
    let count = 0;
    const filteredData = getFilteredGatewayData();
    filteredData.forEach((gateway: GatewayData) => {
      if (gateway && gateway.nodes && Array.isArray(gateway.nodes)) {
        gateway.nodes.slice(0, 10).forEach((value, index) => {
          const isWorking =
            gateway.nodes_status &&
            Array.isArray(gateway.nodes_status) &&
            gateway.nodes_status.length > index &&
            gateway.nodes_status[index] !== 0;

          // Only count if sensor is working and value is not zero
          if (isWorking && value !== 0) {
            count++;
          }
        });
      }
    });
    return count;
  };

  const countEmptyGateways = (): number => {
    let count = 0;
    const filteredData = getFilteredGatewayData();
    filteredData.forEach((gateway: GatewayData) => {
      if (gateway && gateway.nodes && Array.isArray(gateway.nodes)) {
        gateway.nodes.slice(0, 10).forEach((value, index) => {
          const isWorking =
            gateway.nodes_status &&
            Array.isArray(gateway.nodes_status) &&
            gateway.nodes_status.length > index &&
            gateway.nodes_status[index] !== 0;

          // Only count if sensor is working and value is zero
          if (isWorking && value === 0) {
            count++;
          }
        });
      }
    });
    return count;
  };

  const countTotalSensors = (): number => {
    const filteredData = getFilteredGatewayData();
    return filteredData.length * 10; // 10 sensors per DTU
  };

  // Count connected DTUs (DTUs with recent data)
  const countConnectedDtus = (): number => {
    const filteredData = getFilteredGatewayData();
    return filteredData.filter(gateway => {
      // Consider DTU connected if it has recent data (within last 24 hours)
      const lastUpdate = new Date(gateway.timestamp || gateway.time || 0);
      const now = new Date();
      const hoursDiff = (now.getTime() - lastUpdate.getTime()) / (1000 * 60 * 60);
      return hoursDiff < 24;
    }).length;
  };

  // Get total DTU count for internet status
  const getTotalDtuCount = (): number => {
    const filteredData = getFilteredGatewayData();
    return filteredData.length;
  };

  // Get internet status text for individual DTU
  const getInternetStatusText = (): string => {
    if (selectedDtu === "all") {
      return `${countConnectedDtus()}/${getTotalDtuCount()}`;
    } else {
      // For individual DTU, check if it's connected
      const filteredData = getFilteredGatewayData();
      if (filteredData.length > 0) {
        const dtu = filteredData[0];
        const lastUpdate = new Date(dtu.timestamp || dtu.time || 0);
        const now = new Date();
        const hoursDiff = (now.getTime() - lastUpdate.getTime()) / (1000 * 60 * 60);
        return hoursDiff < 24 ? "Connected" : "Not Connected";
      }
      return "Not Connected";
    }
  };

  // Handle DTU selection from alert box
  const handleAlertDtuClick = (gateway: GatewayData) => {
    // Set the selected DTU to focus on this specific DTU
    setSelectedDtu(gateway.gateway_id);
  };

  // Get unique gateway IDs from gatewayData
  const getUniqueGatewayIds = (): string[] => {
    return Array.from(
      new Set(gatewayData.map((gateway) => gateway.gateway_id))
    ).filter(Boolean);
  };

  // Get available DTUs for dropdown
  const getAvailableDtus = (): Array<{ id: string; name: string }> => {
    const dtus = gatewayData.map((gateway) => ({
      id: gateway.gateway_id,
      name: gateway.alias || gateway.gateway_id,
    }));

    // Sort by name for consistent ordering
    dtus.sort((a, b) => a.name.localeCompare(b.name));

    return [{ id: "all", name: "All DTUs" }, ...dtus];
  };

  // Filter gateway data based on selected DTU
  const getFilteredGatewayData = (): GatewayData[] => {
    let filteredData;
    if (selectedDtu === "all") {
      filteredData = gatewayData;
    } else {
      filteredData = gatewayData.filter((gateway) => gateway.gateway_id === selectedDtu);
    }
    
    // Sort by alias name for consistent ordering
    return sortGatewaysBy(filteredData, sortSpecs, 'descending');
  };

  // Calculate map center based on filtered data
  const getMapCenter = () => {
    const filteredData = getFilteredGatewayData();

    if (selectedDtu === "all") {
      // Calculate center from all DTU positions
      const validCoords = filteredData.filter(
        (gateway) => gateway.lat !== undefined && gateway.lng !== undefined
      );

      if (validCoords.length > 0) {
        const latSum = validCoords.reduce((sum, gateway) => sum + gateway.lat!, 0);
        const lngSum = validCoords.reduce((sum, gateway) => sum + gateway.lng!, 0);

        return {
          lat: latSum / validCoords.length,
          lng: lngSum / validCoords.length,
        };
      }
    } else if (selectedDtu !== "all" && filteredData.length === 1) {
      // Use specific DTU position
      const dtu = filteredData[0];
      if (dtu.lat !== undefined && dtu.lng !== undefined) {
        return { lat: dtu.lat, lng: dtu.lng };
      }
    }

    // Fallback to default map settings
    return mapSettings?.center || { lat: 22.373989, lng: 114.193266 };
  };

  // Get gateways with alerts (water level below minimum)
  // Always show ALL alerts regardless of selected DTU
  const getAlertingGateways = (): GatewayData[] => {
    return gatewayData.filter((gateway) => {
      if (gateway && gateway.nodes && Array.isArray(gateway.nodes)) {
        return gateway.nodes.some((value, index) => {
          const isWorking =
            gateway.nodes_status &&
            Array.isArray(gateway.nodes_status) &&
            gateway.nodes_status.length > index &&
            gateway.nodes_status[index] !== 0;

          // Alert condition: sensor is working but water level is below minimum
          return isWorking && value === 0;
        });
      }
      return false;
    }).sort((a, b) => {
      const aliasA = a.alias || a.gateway_id || '';
      const aliasB = b.alias || b.gateway_id || '';
      return aliasA.localeCompare(aliasB);
    });
  };

  // Get alerts for display
  const alertingGateways = getAlertingGateways();

  // Get sensor last online time
  const getSensorOnlineTime = (gatewayId: string, sensorIndex: number): string => {
    const filteredData = getFilteredGatewayData();
    const gateway = filteredData.find((s) => s.gateway_id === gatewayId);
    if (
      gateway &&
      gateway.sensor_online_time &&
      Array.isArray(gateway.sensor_online_time) &&
      gateway.sensor_online_time.length > sensorIndex
    ) {
      return formatDate(gateway.sensor_online_time[sensorIndex]);
    }
    return "No data";
  };

  // Calculate dynamic zoom level based on DTU distribution
  const calculateDynamicZoom = (): number => {
    if (selectedDtu !== "all") {
      return 20; // Increased from 16 for closer view of individual DTU
    }

    const filteredData = getFilteredGatewayData();
    const validCoords = filteredData.filter(
      (gateway) => gateway.lat !== undefined && gateway.lng !== undefined
    );

    if (validCoords.length <= 1) {
      return 20; // Default zoom for single or no points
    }

    // Calculate bounding box
    const lats = validCoords.map(gateway => gateway.lat!);
    const lngs = validCoords.map(gateway => gateway.lng!);

    const latRange = Math.max(...lats) - Math.min(...lats);
    const lngRange = Math.max(...lngs) - Math.min(...lngs);

    const maxRange = Math.max(latRange, lngRange);
    // Zoom level calculation (approximate)
    if (maxRange > 0.1) return 11;
    if (maxRange > 0.05) return 12;
    if (maxRange > 0.02) return 13.5;
    if (maxRange > 0.01) return 15;
    if (maxRange > 0.005) return 17;
    return 18;
  };

  // Get disconnected DTUs (not connected in last 24 hours)
  const getDisconnectedDtus = (): GatewayData[] => {
    return gatewayData.filter((gateway) => {
      const lastUpdate = new Date(gateway.timestamp || gateway.time || 0);
      const now = new Date();
      const hoursDiff = (now.getTime() - lastUpdate.getTime()) / (1000 * 60 * 60);
      return hoursDiff >= 24;
    }).sort((a, b) => {
      const aliasA = a.alias || a.gateway_id || '';
      const aliasB = b.alias || b.gateway_id || '';
      return aliasA.localeCompare(aliasB);
    });
  };

  // Get DTUs with broken sensors
  const getDtusWithBrokenSensors = (): GatewayData[] => {
    return gatewayData.filter((gateway) => {
      if (gateway && gateway.nodes_status && Array.isArray(gateway.nodes_status)) {
        return gateway.nodes_status.some((status, index) =>
          index < 10 && status === 0
        );
      }
      return false;
    }).sort((a, b) => {
      const aliasA = a.alias || a.gateway_id || '';
      const aliasB = b.alias || b.gateway_id || '';
      return aliasA.localeCompare(aliasB);
    });
  };

      // Row helpers: disconnected > broken > alert > normal
      const getGatewayStatusCounts = (gateway: GatewayData) => {
        const maxSensors = 10;
        let normalCount = 0;
        let alertCount = 0;
        let brokenCount = 0;

        const statusArray = Array.isArray(gateway.nodes_status) ? gateway.nodes_status : [];
        const nodeArray = Array.isArray(gateway.nodes) ? gateway.nodes : [];

        for (let index = 0; index < Math.min(maxSensors, statusArray.length); index += 1) {
          const status = statusArray[index];
          if (status === 0) {
            brokenCount += 1;
            continue;
          }

          const value = nodeArray[index];
          if (value === 0) {
            alertCount += 1;
          } else if (value !== undefined) {
            normalCount += 1;
          }
        }

        return { normalCount, alertCount, brokenCount };
      };

      const getGatewayStatusPercentages = (gateway: GatewayData) => {
        const { normalCount, alertCount, brokenCount } = getGatewayStatusCounts(gateway);
        const total = normalCount + alertCount + brokenCount;

        if (total === 0) {
          return { normalPct: 100, alertPct: 0, brokenPct: 0 };
        }

        return {
          normalPct: (normalCount / total) * 100,
          alertPct: (alertCount / total) * 100,
          brokenPct: (brokenCount / total) * 100,
        };
      };

      // Row helpers: disconnected > broken > alert > normal
      const isDtuDisconnected = (gateway: GatewayData): boolean => {
        const lastUpdate = new Date(gateway.timestamp || gateway.time || 0);
        const now = new Date();
        const hoursDiff = (now.getTime() - lastUpdate.getTime()) / (1000 * 60 * 60);
        return hoursDiff >= 24;
      };

      const hasBrokenSensor = (gateway: GatewayData): boolean => {
        if (gateway && gateway.nodes_status && Array.isArray(gateway.nodes_status)) {
          return gateway.nodes_status.slice(0, 10).some((status) => status === 0);
        }
        return false;
      };

      const hasAlertingSensor = (gateway: GatewayData): boolean => {
        if (gateway && gateway.nodes && Array.isArray(gateway.nodes)) {
          return gateway.nodes.slice(0, 10).some((value, index) => {
            const isWorking =
              gateway.nodes_status &&
              Array.isArray(gateway.nodes_status) &&
              gateway.nodes_status.length > index &&
              gateway.nodes_status[index] !== 0;
            return isWorking && value === 0;
          });
        }
        return false;
      };

      const hasNormalSensor = (gateway: GatewayData): boolean => {
        if (gateway && gateway.nodes && Array.isArray(gateway.nodes)) {
          return gateway.nodes.slice(0, 10).some((value, index) => {
            const isWorking =
              gateway.nodes_status &&
              Array.isArray(gateway.nodes_status) &&
              gateway.nodes_status.length > index &&
              gateway.nodes_status[index] !== 0;
            return isWorking && value !== 0;
          });
        }
        return false;
      };

      const renderStatusIcons = (gateway: GatewayData, includeDisconnected: boolean = true): JSX.Element => {
        const size = 16;

        const Icon = ({ shape, color, label }: { shape: 'triangle' | 'square' | 'diamond' | 'hex'; color: string; label: string; }) => {
          const clipPath =
            shape === 'triangle'
              ? 'polygon(50% 0%, 0% 100%, 100% 100%)'
              : shape === 'diamond'
              ? 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)'
              : shape === 'hex'
              ? 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)'
              : 'none';

          const baseStyle: React.CSSProperties = {
            width: size,
            height: size,
            background: color,
            clipPath,
            borderRadius: shape === 'square' ? 2 : 0,
            textShadow: '0 0 2px rgba(0,0,0,0.9), 0 0 4px rgba(0,0,0,0.6)',
          };

          const dx = label === '!' ? 0.6 : 0; // nudge '!' slightly right to visually center
          const dy = shape === 'triangle' ? 1 : 0; // nudge down inside triangle apex
          const labelStyle: React.CSSProperties = {
            fontSize: 10,
            fontWeight: 800,
            lineHeight: 1,
            color: '#ffffff',
            textShadow: '0 0 2px rgba(0,0,0,0.9), 0 0 4px rgba(0,0,0,0.6)',
            transform: `translate(${dx}px, ${dy}px)`,
            userSelect: 'none',
            pointerEvents: 'none',
          };

          return (
            <div className="relative" style={{ width: size, height: size }}>
              <div className="absolute inset-0" style={baseStyle} />
              <div className="absolute inset-0 flex items-center justify-center" style={labelStyle}>
                {label}
              </div>
            </div>
          );
        };

        const parts: JSX.Element[] = [];
        const broken = hasBrokenSensor(gateway);
        const alerting = hasAlertingSensor(gateway);
        const normal = hasNormalSensor(gateway);
        const disconnected = includeDisconnected && isDtuDisconnected(gateway);

        // Sensor status prioritization: show at most 2 from sensors, and add disconnected as extra when present
        if (disconnected) {
          parts.push(<Icon key="disc" shape="square" color="#000000" label="×" />);
          // Only show disconnected icon for disconnected DTUs
          return <div className="inline-flex items-center gap-1">{parts}</div>;
        }
        
        if (broken && alerting) {
          parts.push(<Icon key="broken" shape="hex" color="#f59e0b" label="!" />);
          parts.push(<Icon key="alert" shape="triangle" color="#ef4444" label="!" />);
        } else if (broken) {
          parts.push(<Icon key="broken" shape="hex" color="#f59e0b" label="!" />);
        } else if (alerting) {
          parts.push(<Icon key="alert" shape="triangle" color="#ef4444" label="!" />);
        } else if (normal) {
          parts.push(<Icon key="normal" shape="diamond" color="#22c55e" label="✓" />);
        }


        return <div className="inline-flex items-center gap-1">{parts}</div>;
      };

      const getRowBarStyle = (gateway: GatewayData, includeDisconnected: boolean = true): React.CSSProperties => {
        const hexToRgba = (hex: string, alpha: number) => {
          const r = parseInt(hex.slice(1, 3), 16);
          const g = parseInt(hex.slice(3, 5), 16);
          const b = parseInt(hex.slice(5, 7), 16);
          return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        };

        const disconnected = includeDisconnected && isDtuDisconnected(gateway);
        
        if (disconnected) {
          return {
            background: `linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,0.35) 100%)`,
            borderRadius: '5px 0 0 5px',
            boxShadow: '0 0 0 0.1px rgba(0,0,0,0.35), 0 0 4px rgba(0,0,0,0.3)',
          };
        }
        
        const { normalPct, alertPct, brokenPct } = getGatewayStatusPercentages(gateway);
        
        const green = '#22c55e';
        const red = '#ef4444';
        const orange = '#f59e0b';
        
        const stopValues = [
          { offset: 0, color: green },
          { offset: normalPct, color: green },
          { offset: normalPct, color: red },
          { offset: normalPct + alertPct, color: red },
          { offset: normalPct + alertPct, color: orange },
          { offset: 100, color: orange },
        ];

        const segments = stopValues
          .map((stop) => {
            // Fade from full opacity (left) to 40% opacity (right), making small segments more visible
            const opacity = Math.max(0.4, 1 - (stop.offset / 100) * 0.6);
            return `${hexToRgba(stop.color, opacity)} ${stop.offset}%`;
          })
          .join(', ');
        
        return {
          background: `linear-gradient(to right, ${segments})`,
          borderRadius: '5px 0 0 5px',
          boxShadow: '0 0 0 0.1px rgba(0,0,0,0.35), 0 0 4px rgba(0,0,0,0.3)',
        };
      };


      // Legend shapes for Status column (matches renderStatusIcons)
      const LegendIcon = ({ shape, color, label, size = 14 }: { shape: 'triangle' | 'square' | 'diamond' | 'hex'; color: string; label: string; size?: number; }) => {
        const clipPath =
          shape === 'triangle'
            ? 'polygon(50% 0%, 0% 100%, 100% 100%)'
            : shape === 'diamond'
            ? 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)'
            : shape === 'hex'
            ? 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)'
            : 'none';

        const baseStyle: React.CSSProperties = {
          width: size,
          height: size,
          background: color,
          clipPath,
          borderRadius: shape === 'square' ? 2 : 0,
        };

        const dx = label === '!' ? 0.6 : 0;
        const dy = shape === 'triangle' ? 1 : 0;
        const labelStyle: React.CSSProperties = {
          fontSize: 9,
          fontWeight: 800,
          lineHeight: 1,
          color: '#ffffff',
          textShadow: '0 0 2px rgba(0,0,0,0.9), 0 0 4px rgba(0,0,0,0.6)',
          transform: `translate(${dx}px, ${dy}px)`,
          userSelect: 'none',
          pointerEvents: 'none',
        };

        return (
          <div className="relative" style={{ width: size, height: size }}>
            <div className="absolute inset-0" style={baseStyle} />
            <div className="absolute inset-0 flex items-center justify-center" style={labelStyle}>
              {label}
            </div>
          </div>
        );
      };


      // Original dot legend for sensor cells
      const DotLegend = ({ size = 14, className = '' }: { size?: number; className?: string; }) => (
        <div className={`flex flex-col gap-2 text-xs text-gray-600 ${className}`}>
          <div className="flex items-center gap-2">
            <span className="inline-block rounded-full border border-black/40 shadow" style={{ width: size, height: size, background: '#22c55e' }} />
            <span>Normal level</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-block rounded-full border border-black/40 shadow" style={{ width: size, height: size, background: '#ef4444' }} />
            <span>Low level</span>
          </div>
          <div className="flex items-center gap-2">
            <span 
              className="inline-block rounded-full border border-black/40 shadow" 
              style={{ 
                width: size, 
                height: size, 
                background: 'repeating-linear-gradient(45deg, #ff8c00, #ff8c00 2px, #000000 2px, #000000 4px)' 
              }} 
            />
            <span>Malfunction sensors</span>
          </div>
        </div>
      );

      const StatusShapesLegend: React.FC<{ className?: string; includeDisconnected?: boolean }> = ({ className = '', includeDisconnected = true }) => {
        const [openKey, setOpenKey] = useState<string | null>(null);

        const items: { key: string; shape: 'triangle' | 'square' | 'diamond' | 'hex'; color: string; label: string; desc: string }[] = [
          {
            key: 'normal',
            shape: 'diamond',
            color: '#22c55e',
            label: 'Normal level',
            desc: 'Normal level: All the water barrier in that specific DTU are filled with sufficient water.'
          },
          {
            key: 'low',
            shape: 'triangle',
            color: '#ef4444',
            label: 'Low level',
            desc: 'Low level: Some of the water barrier in that specific DTU are filled with insufficient water.'
          },
          {
            key: 'malfunction',
            shape: 'hex',
            color: '#f59e0b',
            label: 'Malfunction sensors',
            desc: 'Malfunction sensors: Some of the sensors in that specific DTU are unable to send back the water status signal.'
          }
        ];

        if (includeDisconnected) {
          items.push({
            key: 'disconnected',
            shape: 'square',
            color: '#000000',
            label: 'DTU disconnected',
            desc: 'DTU disconnected: That specific DTU is not able to wake up & run sampling for more than 24 hours.'
          });
        }

        return (
          <div className={`flex flex-col gap-2 text-xs text-gray-600 ${className}`}>
            {items.map((it) => (
              <div key={it.key} className="relative">
                <button
                  onClick={() => setOpenKey((k) => (k === it.key ? null : it.key))}
                  title={it.desc}
                  className="flex items-center gap-2 w-full text-left"
                >
                  <LegendIcon shape={it.shape} color={it.color} label={it.key === 'normal' ? '✓' : it.key === 'disconnected' ? '×' : '!'} />
                  <span>{it.label}</span>
                </button>
                {openKey === it.key && (
                  <div className="absolute mt-1 w-64 bg-white border rounded shadow p-2 text-xs text-gray-800 z-50">
                    {it.desc}
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      };







  // Add function to load DTU history
  const loadDtuHistory = async (dtuAlias: string) => {
    if (dtuAlias === 'all') return;

    setLoadingHistory(true);
    try {
      const history = await fetchMultipleDtuHistoryFromSupabase([dtuAlias]);
      setDtuHistory(computeGatewayMetricsAll(history));
    } catch (error) {
      console.error('Error loading DTU history:', error);
      setDtuHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Load history groups for ALL DTUs (28 days) and cache the grouped result
  const loadAllDtuHistory = async (days: number = 28) => {
    setLoadingAllHistory(true);
    try {
      // fetch grouped history (already computes metrics per group)
      const groups = await fetchAllDtuHistoryGrouped(days);

      // Apply local sorting using current sortSpecs to each group
      const processed = groups.map((group) => sortGatewaysBy(group, sortSpecs, 'descending'));
      setAllDtuHistoryGroups(processed);
    } catch (err) {
      console.error('Error loading all DTU history groups:', err);
      setAllDtuHistoryGroups([]);
    } finally {
      setLoadingAllHistory(false);
    }
  };

  // Add effect to load history when selectedDtu changes
  useEffect(() => {
    if (historyTab === 'history' && selectedDtu !== 'all') {
      loadDtuHistory(selectedDtu);
    }
  }, [selectedDtu, historyTab]);

  // Load grouped history when viewing history tab for all DTUs
  useEffect(() => {
    if (historyTab === 'history' && selectedDtu === 'all') {
      loadAllDtuHistory(28);
    }
  }, [historyTab, selectedDtu]);

  // Re-apply sorting when sortSpecs change (do not refetch)
  useEffect(() => {
    if (allDtuHistoryGroups && allDtuHistoryGroups.length > 0) {
      setAllDtuHistoryGroups((prev) => prev.map((g) => sortGatewaysBy(g, sortSpecs, 'descending')));
    }
  }, [sortSpecs]);

  // Add handler for history tab change
  const handleHistoryTabChange = (tab: 'latest' | 'history') => {
    setHistoryTab(tab);
    if (tab === 'history' && selectedDtu !== 'all') {
      loadDtuHistory(selectedDtu);
    }
  };

  // Add effect to reset to latest tab when DTU selection changes
  useEffect(() => {
    setHistoryTab('latest');
  }, [selectedDtu]);

  // Reset sorting whenever the history tab or selected DTU changes
  useEffect(() => {
    resetSortingToDefault();
    setSortOpen(false);
  }, [historyTab, selectedDtu]);

  // Add functions to count totals across ALL DTUs (not filtered)
  const countAllFilledGateways = (): number => {
    let count = 0;
    gatewayData.forEach((gateway: GatewayData) => {
      if (gateway && gateway.nodes && Array.isArray(gateway.nodes)) {
        gateway.nodes.slice(0, 10).forEach((value, index) => {
          const isWorking = gateway.nodes_status &&
            Array.isArray(gateway.nodes_status) &&
            gateway.nodes_status.length > index &&
            gateway.nodes_status[index] !== 0;
          if (isWorking && value !== 0) {
            count++;
          }
        });
      }
    });
    return count;
  };

  const countAllEmptyGateways = (): number => {
    let count = 0;
    gatewayData.forEach((gateway: GatewayData) => {
      if (gateway && gateway.nodes && Array.isArray(gateway.nodes)) {
        gateway.nodes.slice(0, 10).forEach((value, index) => {
          const isWorking = gateway.nodes_status &&
            Array.isArray(gateway.nodes_status) &&
            gateway.nodes_status.length > index &&
            gateway.nodes_status[index] !== 0;
          if (isWorking && value === 0) {
            count++;
          }
        });
      }
    });
    return count;
  };

  const countAllTotalSensors = (): number => {
    let count = 0;
    gatewayData.forEach((gateway: GatewayData) => {
      if (gateway && gateway.nodes && Array.isArray(gateway.nodes)) {
        gateway.nodes.slice(0, 10).forEach((_, index) => {
          const isWorking = gateway.nodes_status &&
            Array.isArray(gateway.nodes_status) &&
            gateway.nodes_status.length > index;
          if (isWorking) {
            count++;
          }
        });
      }
    });
    return count;
  };

  const getAllBrokenSensorCount = (): number => {
    let brokenCount = 0;
    gatewayData.forEach((gateway: GatewayData) => {
      if (gateway && gateway.nodes_status && Array.isArray(gateway.nodes_status)) {
        gateway.nodes_status.slice(0, 10).forEach((status) => {
          if (status === 0) {
            brokenCount++;
          }
        });
      }
    });
    return brokenCount;
  };

  const getAllInternetStatusText = (): string => {
    const connectedDtus = gatewayData.filter(gateway => {
      const lastUpdate = new Date(gateway.timestamp || gateway.time || 0);
      const now = new Date();
      const hoursDiff = (now.getTime() - lastUpdate.getTime()) / (1000 * 60 * 60);
      return hoursDiff < 24;
    }).length;
    return `${connectedDtus}/${gatewayData.length}`;
  };

  // Render the mobile or desktop layout based on screen size
  return (
    <div className="flex flex-col bg-white text-black min-h-screen">
      {isMobile ? (
        <header className="p-4 bg-gradient-to-r from-cyan-500 via-blue-600 to-blue-700 border-b border-cyan-300/20">
          <div className="flex flex-col items-center relative">
            <button
              onClick={logout}
              className="absolute top-0 right-0 bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-xs"
            >
              Logout
            </button>
            <img
              src="/PipescanAILogo/SVG/full_dark.svg"
              alt="Pipescan AI Logo"
              className="h-10 rounded mb-2"
            />
            <h1 className="text-xl text-white text-center">
              Real-time Monitoring Dashboard
            </h1>
          </div>
        </header>
      ) : (
        <header className="p-4 bg-gradient-to-r from-cyan-500 via-blue-600 to-blue-700 border-b border-cyan-300/20">
          <div className="flex items-center relative">
            <img
              src="/PipescanAILogo/SVG/full_dark.svg"
              alt="Pipescan AI Logo"
              className="h-12 rounded absolute left-0"
            />
            <h1 className="text-2xl text-white w-full text-center">
              Real-time Monitoring Dashboard
            </h1>
            <button
              onClick={logout}
              className="absolute right-0 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm"
            >
              Logout
            </button>
          </div>
        </header>
      )}

      {/* Mobile Layout */}
      {isMobile ? (
        <div className="flex flex-col p-2">
          {/* Mobile Alert sections */}
          <div className="bg-white border-2 border-gray-300 rounded shadow p-2 mb-2">
            {/* Tab headers for mobile */}
            <div className="flex mb-2 border-b">
              <button
                className={`flex-1 py-1 px-2 text-xs font-medium border-b-2 ${
                  attentionTab === 'disconnected'
                    ? 'border-gray-800 text-gray-800 bg-gray-50'
                    : 'border-transparent text-gray-500'
                }`}
                onClick={() => setAttentionTab('disconnected')}
              >
                Disconnected
              </button>
              <button
                className={`flex-1 py-1 px-2 text-xs font-medium border-b-2 ${
                  attentionTab === 'alert'
                    ? 'border-red-500 text-red-700 bg-red-50'
                    : 'border-transparent text-gray-500'
                }`}
                onClick={() => setAttentionTab('alert')}
              >
                Alert
              </button>
              <button
                className={`flex-1 py-1 px-2 text-xs font-medium border-b-2 ${
                  attentionTab === 'broken'
                    ? 'border-gray-600 text-gray-700 bg-gray-50'
                    : 'border-transparent text-gray-500'
                }`}
                onClick={() => setAttentionTab('broken')}
              >
                Malfunction
              </button>
            </div>

            {/* Mobile tab content */}
            {attentionTab === 'alert' && (
              <div>
                <h3 className="text-sm font-bold text-center mb-2 text-red-700">
                  Low Water Level Alerts
                </h3>
                {alertingGateways.length > 0 ? (
                  <div className="max-h-[30vh] overflow-y-auto space-y-1">
                    {alertingGateways.map((gateway) => (
                      <div
                        key={gateway._id}
                        className="bg-red-600 text-white p-2 rounded cursor-pointer hover:bg-red-700"
                        onClick={() => handleAlertDtuClick(gateway)}
                      >
                        <div className="font-bold text-xs">{gateway.alias}</div>
                        <div className="text-xs">
                          Last Online: {formatDate(gateway.timestamp || gateway.time || lastPageUpdate)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center p-2 bg-gray-100 rounded text-xs">
                    No water level alerts
                  </div>
                )}
              </div>
            )}

            {attentionTab === 'disconnected' && (
              <div>
                <h3 className="text-sm font-bold text-center mb-2 text-gray-800">
                  Disconnected DTUs
                </h3>
                {getDisconnectedDtus().length > 0 ? (
                  <div className="max-h-[30vh] overflow-y-auto space-y-1">
                    {getDisconnectedDtus().map((gateway) => (
                      <div
                        key={gateway._id}
                        className="bg-gray-800 text-white p-2 rounded cursor-pointer hover:bg-gray-900"
                        onClick={() => handleAlertDtuClick(gateway)}
                      >
                        <div className="font-bold text-xs">{gateway.alias}</div>
                        <div className="text-xs">
                          Last Online: {formatDate(gateway.timestamp || gateway.time || lastPageUpdate)}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center p-2 bg-gray-100 rounded text-xs">
                    All DTUs connected
                  </div>
                )}
              </div>
            )}

            {attentionTab === 'broken' && (
              <div>
                <h3 className="text-sm font-bold text-center mb-2 text-orange-700">
                  DTUs with Malfunction Sensors
                </h3>
                {getDtusWithBrokenSensors().length > 0 ? (
                  <div className="max-h-[30vh] overflow-y-auto space-y-1">
                    {getDtusWithBrokenSensors().map((gateway) => {
                      const brokenSensors = gateway.nodes_status
                        ?.map((status, index) => index < 10 && status === 0 ? index + 1 : null)
                        .filter(Boolean) || [];

                      return (
                        <div
                          key={gateway._id}
                          className="bg-orange-600 text-white p-2 rounded cursor-pointer hover:bg-orange-700"
                          onClick={() => handleAlertDtuClick(gateway)}
                        >
                          <div className="font-bold text-xs">{gateway.alias}</div>
                          <div className="text-xs">Malfunction: S{brokenSensors.join(', S')}</div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center p-2 bg-gray-100 rounded text-xs">
                    No malfunction sensors
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Summary - moved above View Logs button */}
          <div className="bg-gray-100 rounded shadow p-2 mb-2">
            <h3 className="text-base font-semibold mb-1">Summary</h3>
            <div className="bg-gray-300 p-2 rounded text-sm">
              <div className="grid grid-cols-2 gap-1">
                <div className="font-semibold">Normal Sensors:</div>
                <div>{countAllFilledGateways()}/{countAllTotalSensors()}</div>
                <div className="font-semibold">Alerting Sensors:</div>
                <div>{countAllEmptyGateways()}/{countAllTotalSensors()}</div>
                <div className="font-semibold">Malfunction Sensors:</div>
                <div>
                  {getAllBrokenSensorCount()}/{countAllTotalSensors()}
                </div>
                <div className="font-semibold">Internet:</div>
                <div>{getAllInternetStatusText()}</div>
              </div>
            </div>
          </div>

          {/* View Logs Button - moved below summary */}
          <div className="mb-2">
            <button
              className="w-full bg-blue-600 text-white p-2 rounded text-sm hover:bg-blue-700"
              onClick={() => setShowLogBox(true)}
            >
              View Logs
            </button>
          </div>

          {/* System Status and Quick Stats */}
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div className="bg-gray-100 p-2 rounded shadow">
              <h3 className="text-xs text-gray-600">System Status</h3>
              <div className="text-base text-green-500 font-bold">Normal</div>
            </div>
            <div className="bg-gray-100 p-2 rounded shadow">
              <h3 className="text-xs text-gray-600">Gateways</h3>
              <div className="text-base font-bold">
                {getUniqueGatewayIds().length}
              </div>
            </div>
          </div>

          {/* DTU Selection Dropdown */}
          <div className="mb-2">
            <div className="bg-gray-100 p-2 rounded shadow">
              <h3 className="text-xs text-gray-600 mb-1">Select DTU</h3>
              <select
                value={selectedDtu}
                onChange={(e) => setSelectedDtu(e.target.value)}
                className="w-full p-1 border border-gray-300 rounded bg-white text-xs"
              >
                {getAvailableDtus().map((dtu) => (
                  <option key={dtu.id} value={dtu.id}>
                    {dtu.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Status Counters */}
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div className="bg-gradient-to-r from-emerald-500 to-green-600 p-2 rounded text-white text-center shadow-lg">
              <div className="text-xl font-bold text-white drop-shadow-md">
                {countFilledGateways()} / {countTotalSensors()}
              </div>
              <div className="text-xs text-white font-medium">Normal Counts</div>
            </div>

            <div className="bg-gradient-to-r from-red-400 to-red-500 p-2 rounded text-white text-center shadow-lg">
              <div className="text-xl font-bold text-white drop-shadow-sm">
                {countEmptyGateways()} / {countTotalSensors()}
              </div>
              <div className="text-xs text-white/90 font-medium">Alerting Counts</div>
            </div>

            <div className="bg-gradient-to-r from-orange-400 to-orange-500 p-2 rounded text-white text-center shadow-lg">
              <div className="text-xl font-bold text-white drop-shadow-sm">
                {brokenSensorCount} / {totalSensorCount}
              </div>
              <div className="text-xs text-white/90 font-medium">Malfunction Counts</div>
            </div>

            {/* Location - only show for specific DTU selection */}
            {selectedDtu !== "all" && (
              <div className="bg-gradient-to-r from-blue-400 to-blue-500 p-2 rounded text-white text-center shadow-lg">
                <div className="text-xl font-bold text-white drop-shadow-sm">
                  {getMapCenter().lat.toFixed(4)},{" "}
                  {getMapCenter().lng.toFixed(4)}
                </div>
                <div className="text-xs text-white/90 font-medium">Location</div>
              </div>
            )}

            {selectedDtu === "all" && (
              <div className="bg-gradient-to-r from-gray-500 to-gray-600 p-2 rounded text-white text-center shadow-lg">
                <div className="text-xl font-bold text-white drop-shadow-sm">
                  {getInternetStatusText()}
                </div>
                <div className="text-xs text-white/90 font-medium">Internet</div>
              </div>
            )}

            {selectedDtu !== "all" && (
            <div className="bg-gradient-to-r from-gray-500 to-gray-600 p-2 rounded text-white text-center shadow-lg col-span-2">
              <div className="text-xl font-bold text-white drop-shadow-sm">
                {getInternetStatusText()}
              </div>
              <div className="text-xs text-white/90 font-medium">Internet</div>
            </div>
            )}

          </div>

          {/* Map section */}
          <div className="mb-2">
            <div className="text-base text-blue-600 font-semibold mb-1">
              Layout Map
            </div>
            <div className="bg-gray-100 rounded shadow h-[40vh] relative">
              <GoogleMapLocation
                gatewayData={gatewayData}
                mapCenter={getMapCenter()}
                mapZoom={calculateDynamicZoom()}
                mapHeight="100%"
                lastPageUpdate={lastPageUpdate}
                selectedDtu={selectedDtu}
              />
            </div>
          </div>

          {/* Gateway table - always displayed, with horizontal scroll on mobile */}
          <div className="bg-gray-100 rounded shadow p-2">
            <div className="mb-2 flex justify-between items-start">
              <h3 className="text-base font-semibold">
                Gateway Sensors Status
              </h3>
              {/* History tabs */}
              <div className="flex bg-gray-200 rounded">
                <button
                  className={`px-3 py-1 text-xs rounded-l ${
                    historyTab === 'latest'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                  onClick={() => handleHistoryTabChange('latest')}
                >
                  Latest
                </button>
                <button
                  className={`px-3 py-1 text-xs rounded-r ${
                    historyTab === 'history'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                  onClick={() => handleHistoryTabChange('history')}
                >
                  History
                </button>
              </div>
            </div>

            {/* Expandable Legends for mobile */}
            <div className="mb-2 flex gap-8">
              <div className="relative">
                <div className="text-xs font-medium text-gray-700 mb-2">
                  <span>Overall Status Indication</span>
                </div>
                <div className="bg-white border rounded shadow-lg p-3 min-w-max">
                  <StatusShapesLegend className="" includeDisconnected={historyTab === 'latest'} />
                </div>
              </div>
              
              <div className="relative">
                <div className="text-xs font-medium text-gray-700 mb-2">
                  <span>Sensor Status Indication</span>
                </div>
                <div className="bg-white border rounded shadow-lg p-3 min-w-max">
                  <DotLegend size={6} className="" />
                </div>

                <div className="flex">
                  <div className="w-20 flex justify-stat">
                    <SortDropdown compact />
                  </div>
                  <div className="w-20 flex justify-end">
                    <ExportDropdown gatewayData={gatewayData} />
                  </div>
                </div>
                
              </div>

            </div>

            {/* Tab content */}
            {historyTab === 'latest' ? (
              // Existing latest data table
              <div className={`overflow-x-auto -mx-2 px-2 ${selectedDtu === "all" ? "max-h-[50vh] overflow-y-auto" : ""}`}>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-200">
                      <th className="p-1 text-center"></th>
                          <th className="p-1 text-center">Status</th>
                          <th className="p-1 text-center">Gateway</th>
                      <th className="p-1 text-center">Last Online</th>
                      {Array(10).fill(0).map((_, i) => (
                        <th key={i} className="p-1 text-center">S{i + 1}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredGatewayData().map((gateway) => (
                          <tr
                            key={gateway._id}
                            className={`relative border-b border-gray-200`}
                          >
                            <td className="relative p-0 align-middle">
                              <div className="absolute inset-y-0 left-0 w-[10px]" style={getRowBarStyle(gateway)} />
                              <div className="w-2" />
                            </td>
                        <td className="p-1 text-center">{renderStatusIcons(gateway)}</td>
                        <td className="p-1 text-center">{getGatewayDisplayName(gateway)}</td>
                        <td className="p-1 text-center text-xs">{formatDate(gateway.timestamp || gateway.time || lastPageUpdate)}</td>
                        {Array(10).fill(0).map((_, sensorIndex) => (
                          <td key={sensorIndex} className="p-1 text-center">
                            <div className="relative inline-block">
                              <div
                                className={`w-3 h-3 rounded-full border border-black/40 shadow ${
                                  getWaterStatus(gateway.gateway_id, sensorIndex)
                                    ? "bg-green-500"
                                    : getNodeStatus(gateway.gateway_id, sensorIndex)
                                    ? "bg-red-500"
                                    : "bg-orange-500"
                                }`}
                                style={
                                  !getNodeStatus(gateway.gateway_id, sensorIndex)
                                    ? {
                                        background: 'repeating-linear-gradient(45deg, #ff8c00, #ff8c00 2px, #000000 2px, #000000 4px)'
                                      }
                                    : {}
                                }
                                title={
                                  !getNodeStatus(gateway.gateway_id, sensorIndex)
                                    ? `Sensor malfunction - Last Online: ${getSensorOnlineTime(gateway.gateway_id, sensorIndex)}`
                                    : getWaterStatus(gateway.gateway_id, sensorIndex)
                                    ? `Water level normal - Last Online: ${getSensorOnlineTime(gateway.gateway_id, sensorIndex)}`
                                    : `Water level below minimum - Last Online: ${getSensorOnlineTime(gateway.gateway_id, sensorIndex)}`
                                }
                              />
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              // History data table (per-DTU or grouped for ALL)
              <div className="overflow-x-auto -mx-2 px-2 max-h-64 overflow-y-auto">
                {selectedDtu === 'all' ? (
                  loadingAllHistory ? (
                    <div className="text-center py-4 text-gray-500">Loading grouped history...</div>
                  ) : allDtuHistoryGroups.length === 0 ? (
                    <div className="text-center py-4 text-gray-500">No grouped history available</div>
                  ) : (
                    <div className="flex flex-col gap-4">
                      {allDtuHistoryGroups.map((group, gidx) => (
                        <div key={`g-${gidx}`} className="bg-gray-150 border rounded p-3 shadow hover:shadow-md">
                          <div className="flex items-center justify-between mb-2">
                            <div className="font-medium">Group {gidx + 1}</div>
                            <div className="text-xs text-gray-500">{group.length} DTUs</div>
                          </div>

                          <div className={`overflow-x-auto`}> 
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="bg-gray-200">
                                  <th className="p-1 text-center"></th>
                                  <th className="p-1 text-center">Status</th>
                                  <th className="p-1 text-center">Gateway</th>
                                  <th className="p-1 text-center">Last Online</th>
                                  {Array(10).fill(0).map((_, i) => (
                                    <th key={i} className="p-1 text-center">S{i + 1}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {sortGatewaysBy(group, sortSpecs, 'descending').map((gateway) => (
                                  <tr key={gateway._id} className={`relative border-b border-gray-200`}>
                                    <td className="relative p-0 align-middle">
                                      <div className="absolute inset-y-0 left-0 w-[10px]" style={getRowBarStyle(gateway, false)} />
                                      <div className="w-2" />
                                    </td>
                                    <td className="p-1 text-center">{renderStatusIcons(gateway, false)}</td>
                                    <td className="p-1 text-center">{gateway.alias || gateway.gateway_id}</td>
                                    <td className="p-1 text-center text-xs">{formatDate(gateway.timestamp || gateway.time || lastPageUpdate)}</td>
                                    {Array(10).fill(0).map((_, sensorIndex) => (
                                      <td key={sensorIndex} className="p-1 text-center">
                                        <div className="relative inline-block">
                                          <div
                                            className={`w-3 h-3 rounded-full border border-black/40 shadow ${
                                              gateway.nodes_status?.[sensorIndex] === 0
                                                ? "bg-orange-500"
                                                : gateway.nodes?.[sensorIndex] !== 0
                                                ? "bg-green-500"
                                                : "bg-red-500"
                                            }`}
                                            style={
                                              gateway.nodes_status?.[sensorIndex] === 0
                                                ? {
                                                    background: 'repeating-linear-gradient(45deg, #ff8c00, #ff8c00 2px, #000000 2px, #000000 4px)'
                                                  }
                                                : {}
                                            }
                                            title={
                                              gateway.nodes_status?.[sensorIndex] === 0
                                                ? `Sensor malfunction - Last Online: ${formatDate(gateway.sensor_online_time?.[sensorIndex])}`
                                                : gateway.nodes?.[sensorIndex] !== 0
                                                ? `Water level normal - Last Online: ${formatDate(gateway.sensor_online_time?.[sensorIndex])}`
                                                : `Water level below minimum - Last Online: ${formatDate(gateway.sensor_online_time?.[sensorIndex])}`
                                            }
                                          />
                                        </div>
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                ) : (
                  // Existing per-DTU history table
                  loadingHistory ? (
                    <div className="text-center py-4 text-gray-500">Loading history...</div>
                  ) : dtuHistory.length === 0 ? (
                    <div className="text-center py-4 text-gray-500">No history data available</div>
                  ) : (
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-gray-200">
                          <th className="p-1 text-center"></th>
                          <th className="p-1 text-center">Status</th>
                          <th className="p-1 text-center">Gateway</th>
                          <th className="p-1 text-center">Last Online</th>
                          {Array(10).fill(0).map((_, i) => (
                            <th key={i} className="p-1 text-center">S{i + 1}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sortGatewaysBy(dtuHistory, sortSpecs, 'descending').map((gateway) => (
                          <tr key={gateway._id}>
                            <td className="relative p-0 align-middle">
                              <div className="absolute inset-y-0 left-0 w-[10px]" style={getRowBarStyle(gateway, false)} />
                              <div className="w-2" />
                            </td>
                            <td className="p-1 text-center">{renderStatusIcons(gateway, false)}</td>
                            <td className="p-1 text-center">{gateway.alias}</td>
                            <td className="p-1 text-center text-xs">{formatDate(gateway.timestamp)}</td>
                            {Array(10).fill(0).map((_, sensorIndex) => (
                              <td key={sensorIndex} className="p-1 text-center">
                                <div className="relative inline-block">
                                  <div
                                    className={`w-3 h-3 rounded-full border border-black/40 shadow ${
                                      gateway.nodes_status[sensorIndex] === 0
                                        ? "bg-orange-500"
                                        : gateway.nodes[sensorIndex] !== 0
                                        ? "bg-green-500"
                                        : "bg-red-500"
                                    }`}
                                    style={
                                      gateway.nodes_status[sensorIndex] === 0
                                        ? {
                                            background: 'repeating-linear-gradient(45deg, #ff8c00, #ff8c00 2px, #000000 2px, #000000 4px)'
                                          }
                                        : {}
                                    }
                                    title={
                                      gateway.nodes_status[sensorIndex] === 0
                                        ? `Sensor malfunction - Last Online: ${formatDate(gateway.sensor_online_time[sensorIndex])}`
                                        : gateway.nodes[sensorIndex] !== 0
                                        ? `Water level normal - Last Online: ${formatDate(gateway.sensor_online_time[sensorIndex])}`
                                        : `Water level below minimum - Last Online: ${formatDate(gateway.sensor_online_time[sensorIndex])}`
                                    }
                                  />
                                </div>
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Desktop Layout - Unchanged from original */
        <div className="flex flex-row flex-1 p-4">
          {/* Left sidebar */}
          <div className="w-1/6 bg-gray-100 p-4 rounded shadow mr-4">
            <div className="mb-4">
              <h3 className="text-sm text-gray-600">System Status</h3>
              <div className="text-lg text-green-500 font-bold">Normal</div>
            </div>

            {/* Two-column layout for Gateways and Sensors */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <h3 className="text-sm text-gray-600">Gateways</h3>
                <div className="text-lg font-bold">
                  {getUniqueGatewayIds().length}
                </div>
              </div>
              <div>
                <h3 className="text-sm text-gray-600">Sensors</h3>
                <div className="text-lg font-bold">{countTotalSensors()}</div>
              </div>
            </div>

            {/* DTU Selection Dropdown */}
            <div className="mb-4">
              <h3 className="text-sm text-gray-600 mb-2">Select DTU</h3>
              <select
                value={selectedDtu}
                onChange={(e) => setSelectedDtu(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded bg-white text-sm"
              >
                {getAvailableDtus().map((dtu) => (
                  <option key={dtu.id} value={dtu.id}>
                    {dtu.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <button
                className="w-full bg-blue-600 text-white p-2 rounded hover:bg-blue-700"
                onClick={() => setShowLogBox(true)}
              >
                View Logs
              </button>
            </div>

            {/* Stats boxes */}
            <div className="bg-gradient-to-r from-emerald-500 to-green-600 p-4 rounded text-white text-center mb-4 shadow-lg">
              <div className="text-3xl font-bold text-white drop-shadow-md">
                {countFilledGateways()} / {countTotalSensors()}
              </div>
              <div className="text-sm text-white font-medium">Normal Counts</div>
            </div>

            <div className="bg-gradient-to-r from-red-400 to-red-500 p-4 rounded text-white text-center mb-4 shadow-lg">
              <div className="text-3xl font-bold text-white drop-shadow-sm">
                {countEmptyGateways()} / {countTotalSensors()}
              </div>
              <div className="text-sm text-white/90 font-medium">Alerting Counts</div>
            </div>

            {/* Yellow box for Malfunction Counts */}
            <div className="bg-gradient-to-r from-orange-400 to-orange-500 p-4 rounded text-white text-center mb-4 shadow-lg">
              <div className="text-3xl font-bold text-white drop-shadow-md">
                {brokenSensorCount} / {totalSensorCount}
              </div>
              <div className="text-sm text-white font-medium">Malfunction Counts</div>
            </div>

            {/* Location - only show for specific DTU selection */}
            {selectedDtu !== "all" && (
              <div className="bg-gradient-to-r from-blue-400 to-blue-500 p-4 rounded text-white text-center mb-4 shadow-lg">
                <div className="text-2xl font-bold text-white drop-shadow-sm">
                  {getMapCenter().lat.toFixed(4)}{" "},{" "}
                  {getMapCenter().lng.toFixed(4)}
                </div>
                <div className="text-sm text-white/90 font-medium">Location</div>
              </div>
            )}

            <div className="bg-gradient-to-r from-gray-500 to-gray-600 p-4 rounded text-white text-center shadow-lg">
              <div className="text-2xl font-bold text-white drop-shadow-sm">
                {getInternetStatusText()}
              </div>
              <div className="text-sm text-white/90 font-medium">Internet</div>
            </div>
          </div>

          {/* Main content */}
          <div className="flex flex-col w-4/6" id="main-content">
            {/* Map section */}
            <div className="mb-4" id="main-layout-map">
              <div className="text-lg text-blue-600 font-semibold mb-2">
                Layout Map
              </div>
              <div className="bg-gray-100 rounded shadow h-[70vh] relative">
                <GoogleMapLocation
                  gatewayData={gatewayData}
                  mapCenter={getMapCenter()}
                  mapZoom={calculateDynamicZoom()}
                  mapHeight="100%"
                  lastPageUpdate={lastPageUpdate}
                  selectedDtu={selectedDtu}
                />
              </div>
            </div>

            {/* Gateway table with history tabs */}
            <div className="mb-4" id="main-dashboard">
              <div className="bg-gray-100 rounded shadow p-4">
                <div className="flex justify-between items-center mb-2">
                  <div className="text-lg font-semibold">
                    Gateway Sensors Status
                  </div>
                  {/* History tabs for desktop */}
                  <div className="flex bg-gray-200 rounded">
                    <button
                      className={`px-4 py-2 text-sm rounded-l ${
                        historyTab === 'latest'
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                      onClick={() => handleHistoryTabChange('latest')}
                    >
                      Latest
                    </button>
                    <button
                      className={`px-4 py-2 text-sm rounded-r ${
                        historyTab === 'history'
                          ? 'bg-blue-500 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                      onClick={() => handleHistoryTabChange('history')}
                    >
                      History
                    </button>
                  </div>
                </div>

                {/* Expandable Legends for desktop */}
                <div className="mb-4 flex gap-4">
                  <div className="relative">
                    <div className="text-sm font-medium text-gray-700 mb-2">
                      <span>Overall Status Indication</span>
                    </div>
                    <div className="bg-white border rounded shadow-lg p-3 min-w-max">
                      <StatusShapesLegend className="" includeDisconnected={historyTab === 'latest'} />
                    </div>
                  </div>
                  
                  <div className="relative">
                    <div className="text-sm font-medium text-gray-700 mb-2">
                      <span>Sensor Status Indication</span>
                    </div>
                    <div className="bg-white border rounded shadow-lg p-3 min-w-max">
                      <DotLegend className="" />
                    </div>
                  </div>

                  <div className="ml-auto flex items-center gap-2">
                    <ExportDropdown gatewayData={gatewayData} />
                    <SortDropdown />
                  </div>
                </div>

                {/* Tab content */}
                {historyTab === 'latest' ? (
                  // Existing latest data table
                  <div className={`overflow-x-auto ${selectedDtu === "all" ? "max-h-[60vh] overflow-y-auto" : ""}`}>
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-200">
                          <th className="p-2 text-center"></th>
                              <th className="p-2 text-center">Status</th>
                              <th className="p-2 text-center">Gateway</th>
                          <th className="p-2 text-center">Last Online</th>
                          {Array(10)
                            .fill(0)
                            .map((_, i) => (
                              <th key={i} className="p-2 text-center">
                                Sensor {i + 1}
                              </th>
                            ))}
                        </tr>
                      </thead>
                      <tbody>
                        {getFilteredGatewayData().map((gateway) => (
                          <tr
                            key={gateway._id}
                            className={`border-b border-gray-200`}
                          >
                            <td className="relative p-0 align-middle">
                              <div className="absolute inset-y-0 left-0 w-[10px]" style={getRowBarStyle(gateway)} />
                              <div className="w-2" />
                            </td>
                            <td className="p-2 text-center">{renderStatusIcons(gateway)}</td>
                            <td className="p-2 text-center">
                              {getGatewayDisplayName(gateway)}
                            </td>
                            <td className="p-2 text-center">
                              {formatDate(
                                gateway.timestamp || gateway.time || lastPageUpdate
                              )}
                            </td>
                            {Array(10)
                              .fill(0)
                              .map((_, sensorIndex) => (
                                <td key={sensorIndex} className="p-2 text-center">
                                  <div className="relative inline-block">
                                    <div
                                      className={`w-4 h-4 rounded-full border border-black/40 shadow ${
                                        getWaterStatus(gateway.gateway_id, sensorIndex)
                                          ? "bg-green-500"
                                          : getNodeStatus(gateway.gateway_id, sensorIndex)
                                          ? "bg-red-500"
                                          : "bg-orange-500"
                                      }`}
                                      style={
                                        !getNodeStatus(gateway.gateway_id, sensorIndex)
                                          ? {
                                              background: 'repeating-linear-gradient(45deg, #ff8c00, #ff8c00 2px, #000000 2px, #000000 4px)'
                                            }
                                          : {}
                                      }
                                      title={
                                        !getNodeStatus(gateway.gateway_id, sensorIndex)
                                          ? `Sensor malfunction - Last Online: ${formatDate(
                                              gateway.sensor_online_time?.[sensorIndex] || lastPageUpdate
                                            )}`
                                          : getWaterStatus(gateway.gateway_id, sensorIndex)
                                          ? `Water level normal - Last Online: ${formatDate(
                                              gateway.sensor_online_time?.[sensorIndex] || lastPageUpdate
                                            )}`
                                          : `Water level below minimum - Last Online: ${formatDate(
                                              gateway.sensor_online_time?.[sensorIndex] || lastPageUpdate
                                            )}`
                                      }
                                    />
                                  </div>
                                </td>
                              ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  // History data table for desktop (per-DTU or grouped for ALL)
                  <div className="overflow-x-auto max-h-96 overflow-y-auto">
                    {selectedDtu === 'all' ? (
                      loadingAllHistory ? (
                        <div className="text-center py-8 text-gray-500">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-500 mx-auto mb-4"></div>
                          Loading grouped history...
                        </div>
                      ) : allDtuHistoryGroups.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">No grouped history available</div>
                      ) : (
                        <div className="flex flex-col gap-6">
                          {allDtuHistoryGroups.map((group, gidx) => (
                            <div key={`g-${gidx}`} className="bg-gray-150 border rounded p-3 shadow hover:shadow-md">
                              <div className="flex items-center justify-between mb-2">
                                <div className="font-semibold">Group {gidx + 1}</div>
                                <div className="text-xs text-gray-500">{group.length} DTUs</div>
                              </div>

                              <div className="overflow-x-auto">
                                <table className="w-full">
                                  <thead>
                                    <tr className="bg-gray-200">
                                      <th className="p-2 text-center"></th>
                                      <th className="p-2 text-center">Status</th>
                                      <th className="p-2 text-center">Gateway</th>
                                      <th className="p-2 text-center">Last Online</th>
                                      {Array(10)
                                        .fill(0)
                                        .map((_, i) => (
                                          <th key={i} className="p-2 text-center">
                                            Sensor {i + 1}
                                          </th>
                                        ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {sortGatewaysBy(group, sortSpecs, 'descending').map((gateway) => (
                                      <tr key={gateway._id} className={`border-b border-gray-200 bg-gray-50`}>
                                        <td className="relative p-0 align-middle">
                                          <div className="absolute inset-y-0 left-0 w-[10px]" style={getRowBarStyle(gateway, false)} />
                                          <div className="w-2" />
                                        </td>
                                        <td className="p-2 text-center">{renderStatusIcons(gateway, false)}</td>
                                        <td className="p-2 text-center">{gateway.alias || gateway.gateway_id}</td>
                                        <td className="p-2 text-center">{formatDate(gateway.timestamp || gateway.time || lastPageUpdate)}</td>
                                        {Array(10)
                                          .fill(0)
                                          .map((_, sensorIndex) => (
                                            <td key={sensorIndex} className="p-2 text-center">
                                              <div className="relative inline-block">
                                                <div
                                                  className={`w-4 h-4 rounded-full border border-black/40 shadow ${
                                                    gateway.nodes_status?.[sensorIndex] === 0
                                                      ? "bg-orange-500"
                                                      : gateway.nodes?.[sensorIndex] !== 0
                                                      ? "bg-green-500"
                                                      : "bg-red-500"
                                                  }`}
                                                  style={
                                                    gateway.nodes_status?.[sensorIndex] === 0
                                                      ? {
                                                          background: 'repeating-linear-gradient(45deg, #ff8c00, #ff8c00 2px, #000000 2px, #000000 4px)'
                                                        }
                                                      : {}
                                                  }
                                                  title={
                                                    gateway.nodes_status?.[sensorIndex] === 0
                                                      ? `Sensor malfunction - Last Online: ${formatDate(gateway.sensor_online_time?.[sensorIndex])}`
                                                      : gateway.nodes?.[sensorIndex] !== 0
                                                      ? `Water level normal - Last Online: ${formatDate(gateway.sensor_online_time?.[sensorIndex])}`
                                                      : `Water level below minimum - Last Online: ${formatDate(gateway.sensor_online_time?.[sensorIndex])}`
                                                  }
                                                />
                                              </div>
                                            </td>
                                          ))}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          ))}
                        </div>
                      )
                    ) : (
                      loadingHistory ? (
                        <div className="text-center py-8 text-gray-500">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-500 mx-auto mb-4"></div>
                          Loading history...
                        </div>
                      ) : dtuHistory.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">No history data available</div>
                      ) : (
                        <table className="w-full">
                          <thead>
                            <tr className="bg-gray-200">
                              <th className="p-2 text-center"></th>
                                  <th className="p-2 text-center">Status</th>
                                  <th className="p-2 text-center">Gateway</th>
                              <th className="p-2 text-center">Last Online</th>
                              {Array(10).fill(0).map((_, i) => (
                                <th key={i} className="p-2 text-center">Sensor {i + 1}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {sortGatewaysBy(dtuHistory, sortSpecs, 'descending').map((gateway) => (
                              <tr key={gateway._id}>
                                <td className="relative p-0 align-middle">
                                  <div className="absolute inset-y-0 left-0 w-[10px]" style={getRowBarStyle(gateway, false)} />
                                  <div className="w-2" />
                                </td>
                                <td className="p-2 text-center">{renderStatusIcons(gateway, false)}</td>
                                <td className="p-2 text-center">{gateway.alias}</td>
                                <td className="p-2 text-center">{formatDate(gateway.timestamp)}</td>
                                {Array(10).fill(0).map((_, sensorIndex) => (
                                  <td key={sensorIndex} className="p-2 text-center">
                                    <div className="relative inline-block">
                                      <div
                                        className={`w-4 h-4 rounded-full border border-black/40 shadow ${
                                          gateway.nodes_status[sensorIndex] === 0
                                            ? "bg-orange-500"
                                            : gateway.nodes[sensorIndex] !== 0
                                            ? "bg-green-500"
                                            : "bg-red-500"
                                        }`}
                                        style={
                                          gateway.nodes_status[sensorIndex] === 0
                                            ? {
                                                background: 'repeating-linear-gradient(45deg, #ff8c00, #ff8c00 2px, #000000 2px, #000000 4px)'
                                              }
                                            : {}
                                        }
                                        title={
                                          gateway.nodes_status[sensorIndex] === 0
                                            ? `Sensor malfunction - Last Online: ${formatDate(gateway.sensor_online_time[sensorIndex])}`
                                            : gateway.nodes[sensorIndex] !== 0
                                            ? `Water level normal - Last Online: ${formatDate(gateway.sensor_online_time[sensorIndex])}`
                                            : `Water level below minimum - Last Online: ${formatDate(gateway.sensor_online_time[sensorIndex])}`
                                        }
                                      />
                                    </div>
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right sidebar - Alerts with tabs */}
          <div className="w-1/6 ml-4">
            <div className="bg-gray-100 border-2 border-gray-300 rounded shadow p-4 mb-4">
              {/* Tab headers */}
              <div className="flex mb-4 border-b overflow-x-auto">
                <button
                  className={`flex-1 py-2 px-3 text-sm font-medium border-b-2 whitespace-nowrap text-center ${
                    attentionTab === 'disconnected'
                      ? 'border-gray-800 text-gray-800 bg-gray-50'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setAttentionTab('disconnected')}
                >
                  Disconnected
                </button>
                <button
                  className={`flex-1 py-2 px-3 text-sm font-medium border-b-2 whitespace-nowrap text-center ${
                    attentionTab === 'alert'
                      ? 'border-red-500 text-red-700 bg-red-50'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setAttentionTab('alert')}
                >
                  Alert
                </button>
                <button
                  className={`flex-1 py-2 px-3 text-sm font-medium border-b-2 whitespace-nowrap text-center ${
                    attentionTab === 'broken'
                      ? 'border-orange-600 text-orange-700 bg-orange-50'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setAttentionTab('broken')}
                >
                  Malfunction
                </button>
              </div>

              {/* Tab content */}
              <div className="min-h-[200px]">
                {attentionTab === 'alert' && (
                  <div>
                    <h3 className="text-lg font-bold text-center mb-3 text-red-700">
                      Low Water Level Alerts
                    </h3>
                    {alertingGateways.length > 0 ? (
                      <div className="max-h-[40vh] overflow-y-auto space-y-3">
                        {alertingGateways.map((gateway) => (
                          <div
                            key={gateway._id}
                            className="bg-red-600 text-white p-3 rounded cursor-pointer hover:bg-red-700"
                            onClick={() => handleAlertDtuClick(gateway)}
                          >
                            <div className="font-bold">{gateway.alias}</div>
                            <div className="text-sm">
                              Last Online: {formatDate(gateway.timestamp || gateway.time || lastPageUpdate)}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center p-3 bg-gray-100 rounded">
                        No water level alerts
                      </div>
                    )}
                  </div>
                )}

                {attentionTab === 'disconnected' && (
                  <div>
                    <h3 className="text-lg font-bold text-center mb-3 text-gray-800">
                      Disconnected DTUs
                    </h3>
                    {getDisconnectedDtus().length > 0 ? (
                      <div className="max-h-[40vh] overflow-y-auto space-y-3">
                        {getDisconnectedDtus().map((gateway) => (
                          <div
                            key={gateway._id}
                            className="bg-gray-800 text-white p-3 rounded cursor-pointer hover:bg-gray-900"
                            onClick={() => handleAlertDtuClick(gateway)}
                          >
                            <div className="font-bold">{gateway.alias}</div>
                            <div className="text-sm">
                              Last Online: {formatDate(gateway.timestamp || gateway.time || lastPageUpdate)}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center p-3 bg-gray-100 rounded">
                        All DTUs connected
                      </div>
                    )}
                  </div>
                )}

                {attentionTab === 'broken' && (
                  <div>
                    <h3 className="text-lg font-bold text-center mb-3 text-orange-700">
                      DTUs with Malfunction Sensors
                    </h3>
                    {getDtusWithBrokenSensors().length > 0 ? (
                      <div className="max-h-[40vh] overflow-y-auto space-y-3">
                        {getDtusWithBrokenSensors().map((gateway) => {
                          const brokenSensors = gateway.nodes_status
                            ?.map((status, index) => index < 10 && status === 0 ? index + 1 : null)
                            .filter(Boolean) || [];

                          return (
                            <div
                              key={gateway._id}
                              className="bg-orange-600 text-white p-3 rounded cursor-pointer hover:bg-orange-700"
                              onClick={() => handleAlertDtuClick(gateway)}
                            >
                              <div className="font-bold">{gateway.alias}</div>
                              <div className="text-sm">
                                Malfunction: S{brokenSensors.join(', S')}
                              </div>
                              <div className="text-xs">
                                Last Online: {formatDate(gateway.timestamp || gateway.time || lastPageUpdate)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="text-center p-3 bg-gray-100 rounded">
                        No malfunction sensors
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="bg-gray-100 rounded shadow p-4">
              <h3 className="text-xl font-semibold mb-4">Summary</h3>
              <div className="bg-gray-300 p-3 rounded">
                <div className="mb-4">
                  <div className="font-semibold">Normal Sensors</div>
                  <div>{countAllFilledGateways()}/{countAllTotalSensors()}</div>
                </div>
                <div className="mb-3">
                  <div className="font-semibold">Alerting Sensors</div>
                  <div>{countAllEmptyGateways()}/{countAllTotalSensors()}</div>
                </div>
                <div className="mb-3">
                  <div className="font-semibold">Malfunction Sensors</div>
                  <div>
                    {getAllBrokenSensorCount()}/{countAllTotalSensors()}
                  </div>
                </div>
                <div>
                  <div className="font-semibold">Internet</div>
                  <div>{getAllInternetStatusText()}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      

      <footer className="p-2 bg-gradient-to-r from-cyan-500 via-blue-600 to-blue-700 text-sm text-white text-center mt-auto border-t border-cyan-300/20">
        Water Barrier Monitoring Solution
      </footer>

      {/* Fixed positioned webpage update timestamp */}
      <div className="fixed bottom-4 right-4 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded z-40">
        Webpage updated at: {formatDate(lastDataFetch)}
      </div>

      {/* Log Information Box */}
      {showLogBox && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div
            className={`bg-white rounded-lg shadow-lg p-4 ${
              isMobile ? "w-[95%] max-h-[90vh]" : "w-4/5 max-w-4xl max-h-[80vh]"
            } overflow-y-auto`}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className={`${isMobile ? "text-lg" : "text-xl"} font-bold`}>
                System Logs
              </h2>
              <button
                onClick={() => setShowLogBox(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`${isMobile ? "h-5 w-5" : "h-6 w-6"}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="border rounded">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-100">
                    <th
                      className={`${
                        isMobile ? "p-1 text-xs" : "p-2"
                      } text-left w-48`}
                    >
                      Timestamp
                    </th>
                    <th
                      className={`${
                        isMobile ? "p-1 text-xs" : "p-2"
                      } text-center w-20`}
                    >
                      Normal
                    </th>
                    <th
                      className={`${
                        isMobile ? "p-1 text-xs" : "p-2"
                      } text-center w-20`}
                    >
                      Alerting
                    </th>
                    <th
                      className={`${
                        isMobile ? "p-1 text-xs" : "p-2"
                      } text-center w-24`}
                    >
                      Status
                    </th>
                    <th
                      className={`${
                        isMobile ? "p-1 text-xs" : "p-2"
                      } text-left`}
                    >
                      Alerting Sensors
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {logs
                    // Filter logs to only show unique timestamps
                    .reduce((uniqueLogs, currentLog) => {
                      // Check if we already have a log with this timestamp
                      const timeString = formatDate(currentLog.timestamp);
                      const exists = uniqueLogs.some(
                        (log) => formatDate(log.timestamp) === timeString
                      );

                      // If this timestamp doesn't exist yet, add it to uniqueLogs
                      if (!exists) {
                        uniqueLogs.push(currentLog);
                      }
                      return uniqueLogs;
                    }, [] as LogEntry[])
                    .map((log, index) => (
                      <tr
                        key={index}
                        className={index % 2 === 0 ? "bg-gray-50" : ""}
                      >
                        <td
                          className={`${
                            isMobile ? "p-1 text-xs" : "p-2"
                          } text-left`}
                        >
                          {formatDate(log.timestamp)}
                        </td>
                        <td
                          className={`${
                            isMobile ? "p-1 text-xs" : "p-2"
                          } text-center`}
                        >
                          {log.normalCount}
                        </td>
                        <td
                          className={`${
                            isMobile ? "p-1 text-xs" : "p-2"
                          } text-center`}
                        >
                          {log.alertCount}
                        </td>
                        <td
                          className={`${
                            isMobile ? "p-1 text-xs" : "p-2"
                          } text-center ${
                            log.isAlert
                              ? "text-red-600 font-semibold"
                              : "text-black"
                          }`}
                        >
                          {log.isAlert ? "ALERT" : "Normal"}
                        </td>
                        <td
                          className={`${
                            isMobile ? "p-1 text-xs" : "p-2"
                          } text-left`}
                        >
                          {log.alertingSensors.length > 0 ? (
                            <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                              {(() => {
                                // Group sensors by gateway
                                const groupedSensors: {
                                  [key: string]: string[];
                                } = {};

                                log.alertingSensors.forEach((sensor) => {
                                  const parts = sensor.split("-Sensor");
                                  if (parts.length === 2) {
                                    const gateway = parts[0];
                                    const sensorNum = parts[1];

                                    if (!groupedSensors[gateway]) {
                                      groupedSensors[gateway] = [];
                                    }
                                    groupedSensors[gateway].push(sensorNum);
                                  }
                                });

                                // Return the grouped sensors as JSX
                                return Object.entries(groupedSensors).map(
                                  ([gateway, sensors], i) => (
                                    <div
                                      key={i}
                                      className="px-2 py-1 bg-red-100 rounded border border-red-300 text-red-700 mr-1 mb-1"
                                    >
                                      <span className="font-semibold">
                                        {gateway}:
                                      </span>{" "}
                                      S{sensors.join(", S")}
                                    </div>
                                  )
                                );
                              })()}
                            </div>
                          ) : (
                            <span className="text-gray-500">None</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  {logs.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className={`${
                          isMobile ? "p-2 text-xs" : "p-4"
                        } text-center text-gray-500`}
                      >
                        No logs available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
